import { GraphicalClientRequestContextBase } from "abweb/dto/generated/gds/GraphicalClientRequestContextBase";
export class GraphicalClientRequestContext extends GraphicalClientRequestContextBase {
  constructor() {
    super();
    this.collapsedNodes = [];
    this.expandedNodes = [];
    this.walkedNodes = [];
    this.exposedNodes = [];
    this.hiddenNodes = [];
    this.filterDownstream = [];
    this.filterUpstream = [];
  }

  /**
   * Configures collections representing actions performed on a node. Actions typically push a node id onto a list, and
   * the server uses those lists to inform how to retrieve a GraphicalDataSource.
   *
   * Most actions have an inverse action.
   * For example, expanding a node has a collapse action as its inverse. Two lists are maintained in this case - one
   * list holds the explicitly expanded nodes, and another list holds the explicitly collapsed nodes. When expanding a
   * node, we first have to look to see if the inverse action was explicitly performed by looking at the collapse list.
   * If the node was explicitly collapsed, it doesn't get added to the expand list. Instead, it is removed from the
   * collapse list.
   *
   * Some actions only work in one direction.
   * For example, walking a node (at least at the time this was written) does not support un-walking a node. So, there
   * is simply one list, and no inverse list.
   *
   * @param {string} nodeID Identifier of node acted on
   * @param {string[]} collection The list containing nodes on which the action is to be performed.
   * @param {string[]} inverseCollection The list containing nodes where the inverse action will be performed. (optional)
   * @param {boolean} touchBothCollections (default = false) If true, examine both the collection AND inverseCollection.
   * If false, if the node is in the inverseCollection, it is removed and we return.
   * @returns {boolean} true if a change in either the collection or inverseCollection is made.
   */
  static performActionOnNode(nodeID, collection, inverseCollection, touchBothCollections = false) {
    const index = inverseCollection == null ? -1 : inverseCollection.indexOf(nodeID);
    if (index > -1) {
      inverseCollection.splice(index, 1);
      if (!touchBothCollections) {
        return true;
      }
    }
    if (collection.indexOf(nodeID) === -1) {
      collection.push(nodeID);
      return true;
    }
    return false;
  }
  static toggleActionOnNode(nodeID, collection) {
    const index = collection.indexOf(nodeID);
    if (index > -1) {
      collection.splice(index, 1);
    } else {
      collection.push(nodeID);
    }
    return true;
  }
  expandNode(nodeID) {
    return GraphicalClientRequestContext.performActionOnNode(nodeID, this.expandedNodes, this.collapsedNodes);
  }
  collapseNode(nodeID) {
    return GraphicalClientRequestContext.performActionOnNode(nodeID, this.collapsedNodes, this.expandedNodes);
  }
  exposeNode(nodeID) {
    return GraphicalClientRequestContext.performActionOnNode(nodeID, this.exposedNodes, this.hiddenNodes, true);
  }
  isNodeExplicitlyExposed(nodeID) {
    return this.exposedNodes != null && this.exposedNodes.indexOf(nodeID) > -1;
  }
  hideNode(nodeID) {
    return GraphicalClientRequestContext.performActionOnNode(nodeID, this.hiddenNodes, this.exposedNodes, true);
  }
  isNodeExplicitlyHidden(nodeID) {
    return this.hiddenNodes != null && this.hiddenNodes.indexOf(nodeID) > -1;
  }
  walkNode(nodeID) {
    return GraphicalClientRequestContext.performActionOnNode(nodeID, this.walkedNodes, null);
  }

  /**
   * Removes a node from one of the maintained lists, eg, expandedNodes, collapsedNodes)
   * @param {string} nodeID
   * @param {string[]} list Target list
   * @returns {boolean} true if successfully removed, false if not
   */
  removeNodeFromList(nodeID, list) {
    let index = list.indexOf(nodeID);
    if (index < 0) {
      return false;
    }
    list.splice(index, 1);
    return true;
  }
  toggleFilterUpstreamNodes(nodeID) {
    return GraphicalClientRequestContext.toggleActionOnNode(nodeID, this.filterUpstream);
  }
  toggleFilterDownstreamNodes(nodeID) {
    return GraphicalClientRequestContext.toggleActionOnNode(nodeID, this.filterDownstream);
  }
  isFilteredDownstream(nodeID) {
    return this.filterDownstream != null && this.filterDownstream.indexOf(nodeID) > -1;
  }
  isFilteredUpstream(nodeID) {
    return this.filterUpstream != null && this.filterUpstream.indexOf(nodeID) > -1;
  }
}
//# sourceMappingURL=GraphicalClientRequestContext.js.map
